import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import Loader from '../../../common/ui/Loader';
import ListItemWorker from '../../../common/lists/ListItem/ListItemWorker';
import ButtonRounded from '../../../common/forms/buttons/ButtonRounded';
import AddWorkerModal from '../../../common/ui/AddWorkerModal';
import { alertMessages } from '../../../../constants/text';
import { errorCodes } from '../../../../constants/errorCodes';
import { favStatuses } from '../../../../constants/workers';
import mascotImage from '../../../../images/mascot/mascot@2x.png';
import UsersService from '../../../../services/UsersService';
import styled from 'styled-components'

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
`

const Limit = styled.select`
  width: 125px;
  margin-left: auto;
`

export default class Workers extends Component {
  static propTypes = {
    user: PropTypes.object,
    company: PropTypes.object.isRequired,
    workersPredictionsList: PropTypes.array.isRequired,
    getCompanyLTAWorkers: PropTypes.func.isRequired,
    getCompanyFavAndBlocks: PropTypes.func.isRequired,
    searchWorkersAction: PropTypes.func.isRequired,
    searchWorkersForFavorites: PropTypes.func.isRequired,
    clearWorkersPredictionsList: PropTypes.func.isRequired,
    addCompanyLTAWorker: PropTypes.func.isRequired,
    removeCompanyLTAWorker: PropTypes.func.isRequired,
    deleteCompanyWorker: PropTypes.func.isRequired,
    UIShowAlert: PropTypes.func.isRequired,
    UIShowConfirm: PropTypes.func.isRequired
  };

  tabs = {
    favorite: {
      id: favStatuses.favorite,
      title: 'Favorites'
    },
    blocked: {
      id: favStatuses.blocked,
      title: 'Blocked workers'
    },
    lta: {
      id: 'lta',
      title: UsersService.isAdmin() ? 'LTA Workers' : 'Long Term Employees'
    }
  };

  state = {
    isLoading: true,
    addWorkerModalIsOpen: false,
    tabSelected: this.tabs.favorite,
    page: 1,
    limit: 10
  };

  isLtaListSelected = () => this.state.tabSelected.id === this.tabs.lta.id;

  isCompanyLta = () => UsersService.isAdmin() ? this.props.company.is_lta : this.props.user.is_lta;

  _getCompanyId = () => UsersService.isAdmin() ? this.props.company.id : this.props.user.company_id;

  loadWorkers = () => {
    const { limit, page, tabSelected } = this.state
    const { company, getCompanyLTAWorkers, getCompanyFavAndBlocks } = this.props

    this._setDataLoading();

    let loadWorkersPromise;

    if (this.isLtaListSelected()) {
      loadWorkersPromise = getCompanyLTAWorkers(company.id);
    } else {
      loadWorkersPromise = getCompanyFavAndBlocks({ 
        limit,
        offset: ( page - 1 ) * limit,
        company_id: company.id,
        status: tabSelected.id
      });
    }

    return loadWorkersPromise.then(this._setDataLoaded);
  };

  componentDidMount () {
    this.loadWorkers();
  }

  _setDataLoaded = () => {
    this.setState({ isLoading: false });
  };

  _setDataLoading = () => {
    this.setState({ isLoading: true });
  };

  getCompanyWorker = worker =>
    this.props.company.favAndBlocks.find(companyWorker => companyWorker.worker_id === worker.id);

  addWorkersSubmit = selectedWorkers => {
    this._setDataLoading();

    const { addCompanyLTAWorker, createCompanyWorker, updateCompanyWorker } = this.props;
    const actionPromises = [];
    selectedWorkers.forEach(worker => {
      const payload = { company_id: this._getCompanyId(), worker_id: worker.id, status: this.state.tabSelected.id };
      const companyWorker = this.getCompanyWorker(worker);

      const addWorkerRequest = this.isLtaListSelected()
        ? () => addCompanyLTAWorker(payload.worker_id, payload.company_id)
        : companyWorker
          ? () => updateCompanyWorker(companyWorker.id, payload)
          : () => createCompanyWorker(payload);

      actionPromises.push(addWorkerRequest());
    });
    Promise.all(actionPromises).then(data => {
      if (data.error && data.error.code === errorCodes.notAvailableLtaWorker) {
        this.props.UIShowAlert(alertMessages.notAvailableLtaWorker);
        return this._setDataLoaded();
      }

      this.loadWorkers();
    });
  };

  deleteWorkerAction = id => {
    this._setDataLoading();

    if (this.isLtaListSelected()) {
      return this.props.removeCompanyLTAWorker(id);
    }

    return this.props.deleteCompanyWorker(id);
  };

  removeWorker = id => {
    this.props.UIShowConfirm(
      'Are you sure you want to remove this worker?',
      () => this.deleteWorkerAction(id).then(this.loadWorkers)
    );
  };

  openAddWorkerModal = () => this.setState({ addWorkerModalIsOpen: true });

  closeAddWorkerModal = () => this.setState({ addWorkerModalIsOpen: false });

  searchWorkersAction = value => {
    switch (this.state.tabSelected.id) {
      case this.tabs.lta.id:
        return this.props.searchWorkersAction(value, true);
      case this.tabs.blocked.id:
        return this.props.searchWorkersAction(value);
      default:
        return this.props.searchWorkersForFavorites(value, this._getCompanyId());
    }
  };

  setButtonClass = id => this.state.tabSelected.id === id ? 'active' : '';

  selectTab = tabSelected => this.setState({ page: 1, tabSelected }, this.loadWorkers);

  _renderTabs = () => Object.keys(this.tabs).map(tabKey => {
    const tab = this.tabs[tabKey];

    if (tab.id === this.tabs.lta.id && !this.isCompanyLta()) {
      return null;
    }

    return (
      <button
        key={tab.id}
        className={`button-pristine button-tab ${this.setButtonClass(tab.id)}`}
        children={tab.title}
        onClick={() => this.selectTab(tab)}
      />
    );
  });

  _renderWorkersList = () => {
    let companyWorkers = this.props.company[this.isLtaListSelected() ? 'ltaWorkers' : 'favAndBlocks'];

    if (!this.isLtaListSelected()) {
      companyWorkers = companyWorkers.filter(worker => worker.status === this.state.tabSelected.id);
    }

    if (!companyWorkers.length) {
      return (
        <div className='mt-4 text-center'>
          <img className='mascot-image' src={mascotImage} alt='mascot' />
          <div className='mt-2 text-md'>No Workers Yet</div>
        </div>
      );
    }

    return companyWorkers.map(worker => (
      <ListItemWorker
        key={worker.id}
        worker={worker}
        removeWorkerClick={this.removeWorker}
        notEditable={this.areWorkersNotEditable()}
      />
    ));
  };

  areWorkersNotEditable = () => UsersService.isEmployer() && this.isLtaListSelected();

  render () {
    const { company, workersPredictionsList, clearWorkersPredictionsList } = this.props;
    const { addWorkerModalIsOpen, isLoading, page, limit } = this.state;

    return (
      <Fragment>
        <div className='d-flex align-items-center flex-wrap'>
          {this._renderTabs()}

          { !this.areWorkersNotEditable() && <ButtonRounded onClick={this.openAddWorkerModal} className='ml-4' /> }
        </div>

        <div className='d-flex align-items-center'>
          { addWorkerModalIsOpen && (
            <AddWorkerModal
              isOpen
              closeModal={this.closeAddWorkerModal}
              addWorkersSubmit={this.addWorkersSubmit}
              workersPredictionsList={workersPredictionsList}
              searchAction={this.searchWorkersAction}
              clearWorkersPredictionsList={clearWorkersPredictionsList}
            />
          )}
        </div>
        
        {!this.isLtaListSelected() &&
          <Limit
            className='styled-select'
            value={limit}
            onChange={e => this.setState({ page: 1, limit: e.target.value }, () => this.loadWorkers())}
          >
            <option value={5}>5 per page</option>
            <option value={10}>10 per page</option>
            <option value={15}>15 per page</option>
            <option value={20}>20 per page</option>
            <option value={25}>25 per page</option>
          </Limit>
        }

        {isLoading
          ? <Loader /> 
          : <Fragment>
              {this._renderWorkersList()}
              {!this.isLtaListSelected() &&
                <ButtonWrapper>
                  <button
                    disabled={page < 2}
                    className='additional-link non-button'
                    onClick={() => {
                      this.setState(prevState => ({ page: prevState.page - 1 }),
                      () => this.loadWorkers())
                    }}
                  >
                    Previous
                  </button>
                  <button
                    disabled={page >= ( company.count / limit )}
                    className='additional-link non-button'
                    onClick={() => {
                      this.setState(prevState => ({ page: prevState.page + 1 }),
                      () => this.loadWorkers())
                    }}
                  >
                    Next
                  </button>
                </ButtonWrapper>
              }
            </Fragment>
        }
      </Fragment>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/app/companyDetails/Tabs/Workers.js